import React, { memo } from "react";
import { motion } from "framer-motion";
import {
  gameOverContainerVariants,
  gameOverItemVariants,
} from "../animations/variants";
import { UI_STRINGS } from "../strings/uiStrings";
import type { Player } from "../types";

interface TieScreenProps {
  player1: Player;
  player2: Player;
  wins: Record<string, number>;
}

/**
 * Overlay shown when both players finish with the same number of wins.
 */
function TieScreenComponent({
  player1,
  player2,
  wins,
}: TieScreenProps): React.ReactElement {
  return (
    <div
      className="absolute inset-0 flex items-center justify-center pointer-events-none"
      style={{
        zIndex: 50,
        background:
          "radial-gradient(ellipse at center, rgba(0,0,0,0.75) 0%, rgba(0,0,0,0.55) 100%)",
      }}
    >
      <motion.div
        variants={gameOverContainerVariants}
        initial="hidden"
        animate="visible"
        exit="exit"
        className="flex flex-col items-center gap-3"
        style={{ willChange: "opacity" }}
      >
        {/* Tie title */}
        <motion.div
          variants={gameOverItemVariants}
          style={{ willChange: "transform, opacity" }}
        >
          <span
            style={{
              fontFamily: "var(--font-game)",
              fontSize: "4.2rem",
              fontWeight: 900,
              fontStyle: "italic",
              color: "rgba(255,255,255,0.9)",
              lineHeight: 1,
              textShadow:
                "0 0 40px rgba(255,255,255,0.4), 0 4px 12px rgba(0,0,0,0.9)",
              letterSpacing: "-0.02em",
              textTransform: "uppercase",
              userSelect: "none",
            }}
          >
            {UI_STRINGS.tieScreen.title}
          </span>
        </motion.div>

        {/* Subtext */}
        <motion.div
          variants={gameOverItemVariants}
          style={{ willChange: "transform, opacity" }}
        >
          <span
            style={{
              fontFamily: "var(--font-game)",
              fontSize: "1.1rem",
              color: "rgba(255,255,255,0.45)",
              fontStyle: "italic",
              letterSpacing: "0.08em",
              userSelect: "none",
            }}
          >
            {UI_STRINGS.tieScreen.subtext}
          </span>
        </motion.div>

        {/* Score — both players equal */}
        <motion.div
          variants={gameOverItemVariants}
          style={{ willChange: "transform, opacity" }}
          className="flex items-center gap-4"
        >
          <div className="flex flex-col items-center gap-1">
            <span
              style={{
                fontFamily: "var(--font-game)",
                fontSize: "2.4rem",
                fontWeight: 900,
                color: "rgba(255,255,255,0.6)",
                lineHeight: 1,
              }}
            >
              {wins[player1.id] ?? 0}
            </span>
            <span
              style={{
                fontFamily: "var(--font-game)",
                fontSize: "0.7rem",
                color: "rgba(255,255,255,0.4)",
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              {player1.name}
            </span>
          </div>
          <span
            style={{
              fontFamily: "var(--font-game)",
              fontSize: "1.1rem",
              color: "rgba(255,255,255,0.4)",
              fontWeight: 700,
            }}
          >
            {UI_STRINGS.score.vs}
          </span>
          <div className="flex flex-col items-center gap-1">
            <span
              style={{
                fontFamily: "var(--font-game)",
                fontSize: "2.4rem",
                fontWeight: 900,
                color: "rgba(255,255,255,0.6)",
                lineHeight: 1,
              }}
            >
              {wins[player2.id] ?? 0}
            </span>
            <span
              style={{
                fontFamily: "var(--font-game)",
                fontSize: "0.7rem",
                color: "rgba(255,255,255,0.4)",
                fontWeight: 600,
                letterSpacing: "0.06em",
                textTransform: "uppercase",
              }}
            >
              {player2.name}
            </span>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}

export const TieScreen = memo(TieScreenComponent);
